import { useRef } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { Button } from '@/components/ui/button'
import {
  BACKGROUND_CHECK_INTERVAL_MS,
  shouldCheckForUpdate,
} from '@/lib/updateCheck'

/**
 * "There is a newer version — reload."
 *
 * The installed app registers its service worker once, on load, and then
 * never asks again. Left in a dock it will happily run a build from last
 * week: nothing navigates, so nothing triggers a check. This is what asks.
 *
 * It offers rather than acts. Every field in this app commits on blur, so a
 * reload that fired while you were mid-sentence in a notes field would throw
 * the text away — a click costs a second and cannot lose anything.
 */
/** How long to wait for the new worker to take over before reloading anyway. */
const HANDOVER_TIMEOUT_MS = 2_000

/**
 * Hand over to the new worker, then reload.
 *
 * Written out rather than using `updateServiceWorker(true)`, which waits for
 * a controller change before reloading. That change never comes in the case
 * right after a first install: with no controlled page to wait behind, the new
 * worker activates immediately, so there is no waiting worker to skip and no
 * handover to observe — and the button silently does nothing.
 *
 * So: ask a waiting worker to take over if there is one, give it a moment,
 * and reload either way. The reload is the part that actually swaps the code.
 */
async function applyUpdate(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration()
  const waiting = registration?.waiting

  if (waiting !== undefined && waiting !== null) {
    await new Promise<void>((resolve) => {
      const done = () => resolve()
      navigator.serviceWorker.addEventListener('controllerchange', done, {
        once: true,
      })
      // A worker that never activates must not strand you on the old build.
      setTimeout(done, HANDOVER_TIMEOUT_MS)
      waiting.postMessage({ type: 'SKIP_WAITING' })
    })
  }

  window.location.reload()
}

export function UpdateNotice() {
  const lastCheckedAt = useRef<number | null>(null)

  const {
    needRefresh: [needRefresh],
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration === undefined) return

      const check = () => {
        // Focus fires on every window switch; a deploy does not.
        if (
          !shouldCheckForUpdate({
            lastCheckedAt: lastCheckedAt.current,
            now: Date.now(),
          })
        ) {
          return
        }
        lastCheckedAt.current = Date.now()
        void registration.update()
      }

      window.addEventListener('focus', check)
      const timer = setInterval(check, BACKGROUND_CHECK_INTERVAL_MS)

      // Registration outlives this component in practice — the shell is
      // mounted for the life of the app — but leaving a listener and a timer
      // behind on every hot reload in development is its own kind of mess.
      return () => {
        window.removeEventListener('focus', check)
        clearInterval(timer)
      }
    },
  })

  // Nothing to say until a new version is genuinely sitting there waiting.
  if (!needRefresh) return null

  return (
    <div
      role="status"
      className="pb-safe fixed inset-x-0 bottom-16 z-50 flex justify-center px-4 sm:bottom-4"
    >
      <div className="bg-foreground text-background flex items-center gap-3 rounded-md px-3 py-2 text-sm shadow-lg">
        <span>A newer version is ready.</span>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="min-h-8"
          onClick={() => void applyUpdate()}
        >
          Reload
        </Button>
      </div>
    </div>
  )
}
